import { type FC } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import dayjs, { ISO_DATE_FORMAT } from "../../../dayjs";
import type { CustomerSummary } from "../customerSummariesApi";
import { formatDatePl } from "../formatDatePl";
import { useCustomerStatistics } from "../../../admin/statistics/useCustomerStatistics";
import type { CustomerStatisticsResponse } from "../../../admin/statistics/statisticsApi";
import { useAllCustomerNotes } from "../../notes/useAllCustomerNotes";
import type { CustomerNote } from "../../notes/customerNotesApi";
import { useLastOrderDate } from "../useLastOrderDate";
import ClientDialogTitle from "./ClientDialogTitle";

interface Props {
  client: CustomerSummary | null;
  onClose: () => void;
}

interface StatTile {
  label: string;
  value: string | number;
}

const formatEur = (value: number | null) => (value === null ? "—" : `${Math.round(value).toLocaleString("pl-PL")} EUR`);

const averageOrderValue = ({ count, valueEur }: { count: number; valueEur: number | null }) =>
  valueEur === null || count === 0 ? null : valueEur / count;

const countNotes = (notes: CustomerNote[], category: CustomerNote["category"]) =>
  notes.filter((note) => note.category === category).length;

const toStatTiles = (
  statistics: CustomerStatisticsResponse,
  lastOrderDate: string | null,
  notes: CustomerNote[],
): StatTile[] => [
  { label: "Zamówienia YTD", value: statistics.summary.notCancelled.count },
  { label: "Obrót YTD", value: formatEur(statistics.summary.notCancelled.valueEur) },
  { label: "Śr. wartość zamówienia", value: formatEur(averageOrderValue(statistics.summary.notCancelled)) },
  { label: "Ostatnie zamówienie", value: formatDatePl(lastOrderDate) },
  { label: "Notatki Przezmorze", value: countNotes(notes, "PRZEZMORZE") },
  { label: "Notatki TravelPay", value: countNotes(notes, "TRAVEL_PAY") },
];

const StatTiles: FC<{ tiles: StatTile[] }> = ({ tiles }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
    {tiles.map((tile) => (
      <Paper key={tile.label} variant="outlined" sx={{ p: 1.5, bgcolor: "rgba(0,0,0,0.015)" }}>
        <Typography variant="caption" color="text.secondary">
          {tile.label}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {tile.value}
        </Typography>
      </Paper>
    ))}
  </Box>
);

const ClientStatistics: FC<{ client: CustomerSummary }> = ({ client }) => {
  const statistics = useCustomerStatistics(client.id, {
    from: dayjs().startOf("year").format(ISO_DATE_FORMAT),
    includeMargin: false,
  });
  const { notes, isLoading: notesLoading } = useAllCustomerNotes(client.id);
  const lastOrderDate = useLastOrderDate(client.id);
  const loading = statistics.isLoading || notesLoading || lastOrderDate.isLoading;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">{client.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          Statystyki klienta od początku roku
        </Typography>
      </Box>
      {loading && <LinearProgress />}
      {(statistics.isError || lastOrderDate.isError) && (
        <Alert severity="error">Nie udało się pobrać statystyk klienta.</Alert>
      )}
      {statistics.data && lastOrderDate.isSuccess && !notesLoading && (
        <StatTiles tiles={toStatTiles(statistics.data, lastOrderDate.data, notes)} />
      )}
    </Stack>
  );
};

const ClientStatsDialog: FC<Props> = ({ client, onClose }) => (
  <Dialog open={Boolean(client)} onClose={onClose} maxWidth="sm" fullWidth>
    <ClientDialogTitle icon={<BarChartIcon color="primary" />} title="Moduł statystyk" onClose={onClose} />
    <DialogContent dividers>{client && <ClientStatistics client={client} />}</DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Zamknij</Button>
    </DialogActions>
  </Dialog>
);

export default ClientStatsDialog;
